import {
	LineChart, Line, XAxis, YAxis, Tooltip, ReferenceArea, ResponsiveContainer, Brush
} from 'recharts';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Check, Clear, Download, Save } from '@mui/icons-material';
import { AlertColor, Button, IconButton, Input, Typography } from '@mui/material';
import html2canvas from 'html2canvas';
import { chartsConf, chartsConfigData } from '../../../services/api/charts/charts';
import {
	BrushRange,
	BrushDragEndInput,
	buildReadingDates,
	buildReadingLookup,
	formatChartDate,
	formatLimitValue,
	getBrushDragEndPayload,
	getChartDateKey,
	getInitialBrushRange,
} from './chartUtils';

type Entrada = {
	data_leitura: string;
	[key: string]: any;
};

type DadosInstrumento = {
	nome: string;
	leituras: Entrada[];
	corLinha: string;
	yKey: string;
	limites: {
		verde: number | null;
		amarelo: number | null;
		laranja: number | null;
		vermelho: number | null;
	};
};

type Props = {
	instrumentos: DadosInstrumento[];
	legendaY: string;
	corLinha?: string;
	barragem_id: string;
	confGrafico: chartsConfigData[] | null;
	tipo_instrumento: string;
	alert: (menssage: string, severity: AlertColor) => void;
};

function legenda(legendaAtual: string) {
	switch (legendaAtual) {
		case 'cota': return 'Cota (m)';
		case 'nivel': return 'Nivel (m)';
		case 'vazao': return 'Vazao (m3/h)';
		case 'angulo': return 'Angulo';
		case 'deslocamento': return 'Deslocamento (m)';
		default: return legendaAtual;
	}
}

const parseNumber = (value: any) => {
	if (typeof value === 'string') {
		const clean = value.replace(',', '.');
		const parsed = Number(clean);
		return Number.isNaN(parsed) ? null : parsed;
	}

	if (typeof value === 'number') {
		return Number.isNaN(value) ? null : value;
	}

	return null;
};

const getBounds = (values: number[]): [number, number] => {
	if (values.length === 0) {
		return [0, 1];
	}

	const min = Math.min(...values);
	const max = Math.max(...values);

	if (min === max) {
		const margin = Math.abs(min || 1) * 0.1;
		return [min - margin, max + margin];
	}

	return [min, max];
};

const GraficoLinha = ({ instrumentos, legendaY, barragem_id, confGrafico, tipo_instrumento, alert }: Props) => {
	const [selecionados, setSelecionados] = useState<string[]>([]);
	const [yMinCustom, setYMinCustom] = useState<string>('');
	const [yMaxCustom, setYMaxCustom] = useState<string>('');
	const [yMinAplicado, setYMinAplicado] = useState<number | null>(null);
	const [yMaxAplicado, setYMaxAplicado] = useState<number | null>(null);
	const [loading, setLoading] = useState(false);
	const [confId, setConfId] = useState<string | null>(null);
	const [committedBrushRange, setCommittedBrushRange] = useState<BrushRange>({ startIndex: 0, endIndex: 0 });
	const chartRef = useRef<HTMLDivElement>(null);

	const instrumentosOrdenados = [...instrumentos].sort((a, b) => a.nome.localeCompare(b.nome));
	const nomesInstrumentos = instrumentosOrdenados.map((instrumento) => instrumento.nome);
	const todosSelecionados = nomesInstrumentos.length > 0 && nomesInstrumentos.every((nome) => selecionados.includes(nome));

	const toggleInstrumento = (nome: string) => {
		setSelecionados((prev) =>
			prev.includes(nome) ? prev.filter((item) => item !== nome) : [...prev, nome]
		);
	};

	const toggleTodos = () => {
		setSelecionados((prev) => (prev.length === nomesInstrumentos.length ? [] : nomesInstrumentos));
	};

	const handleBrushDragEnd = (event: BrushDragEndInput) => {
		const { startIndex, endIndex } = getBrushDragEndPayload(event);

		setCommittedBrushRange((prev) => {
			const maxIndex = Math.max(dataNormalizada.length - 1, 0);
			const nextStart = typeof startIndex === 'number'
				? Math.max(0, Math.min(startIndex, maxIndex))
				: prev.startIndex;
			const nextEnd = typeof endIndex === 'number'
				? Math.max(nextStart, Math.min(endIndex, maxIndex))
				: prev.endIndex;

			return { startIndex: nextStart, endIndex: nextEnd };
		});
	};

	const downloadChart = async () => {
		if (!chartRef.current || ativos.length === 0) {
			return;
		}

		const canvas = await html2canvas(chartRef.current);
		const link = document.createElement('a');
		link.download = `grafico-${selecionados.join('-')}.png`;
		link.href = canvas.toDataURL('image/png');
		link.click();
	};

	const handleAplicar = () => {
		setLoading(true);
		setTimeout(() => {
			const minVal = parseNumber(yMinCustom);
			const maxVal = parseNumber(yMaxCustom);

			if (minVal !== null && maxVal !== null && minVal < maxVal) {
				setYMinAplicado(minVal);
				setYMaxAplicado(maxVal);
			}
			setLoading(false);
		}, 500);
	};

	const handleLimpar = async () => {
		setLoading(true);
		if (confId) {
			try {
				const res = await chartsConf.delet(confId);
				if (res instanceof Error) {
					alert(`Erro ao deletar: ${res.message}`, 'error');
				} else {
					setTimeout(() => {
						setYMinCustom('');
						setYMaxCustom('');
						setYMinAplicado(null);
						setYMaxAplicado(null);
						setLoading(false);
					}, 500);
				}
			} catch (error) {
				alert(`Erro ao deletar: ${error}`, 'error');
			}
		} else {
			setTimeout(() => {
				setYMinCustom('');
				setYMaxCustom('');
				setYMinAplicado(null);
				setYMaxAplicado(null);
				setLoading(false);
			}, 500);
		}
	};

	const datasLeitura = useMemo(() => buildReadingDates(
		instrumentos.flatMap((instrumento) => instrumento.leituras.map((leitura) => new Date(leitura.data_leitura)))
	), [instrumentos]);
	const leiturasPorInstrumento = useMemo(() => instrumentos.map((instrumento) => ({
		instrumento,
		leiturasPorData: buildReadingLookup(instrumento.leituras),
	})), [instrumentos]);

	const dataNormalizada = useMemo(() => datasLeitura.map((date) => {
		const dateKey = getChartDateKey(date);
		const ponto: any = { data_leitura: dateKey };
		leiturasPorInstrumento.forEach(({ instrumento, leiturasPorData }) => {
			const leitura = leiturasPorData.get(dateKey);
			const valor = leitura ? parseNumber(leitura[instrumento.yKey]) : null;

			if (yMinAplicado !== null && yMaxAplicado !== null && valor !== null) {
				ponto[instrumento.nome] = valor < yMinAplicado || valor > yMaxAplicado ? null : valor;
				return;
			}

			ponto[instrumento.nome] = valor;
		});
		return ponto;
	}), [datasLeitura, leiturasPorInstrumento, yMinAplicado, yMaxAplicado]);

	const initialBrushRange = useMemo(() => getInitialBrushRange(datasLeitura), [datasLeitura]);

	const ativos = instrumentos.filter((instrumento) => selecionados.includes(instrumento.nome));
	const valoresAtivos = ativos.flatMap((instrumento) =>
		instrumento.leituras
			.map((leitura) => parseNumber(leitura[instrumento.yKey]))
			.filter((value): value is number => value !== null)
	);
	const valoresFallback = instrumentos.flatMap((instrumento) =>
		instrumento.leituras
			.map((leitura) => parseNumber(leitura[instrumento.yKey]))
			.filter((value): value is number => value !== null)
	);
	const [minYBase, maxYBase] = getBounds(valoresAtivos.length > 0 ? valoresAtivos : valoresFallback);
	const minYFinal = yMinAplicado !== null ? yMinAplicado : minYBase;
	const maxYFinal = yMaxAplicado !== null ? yMaxAplicado : maxYBase;

	const instrumentoUnico = ativos.length === 1 ? ativos[0] : null;
	const brushStartIndex = dataNormalizada.length > 0 ? Math.min(committedBrushRange.startIndex, dataNormalizada.length - 1) : 0;
	const brushEndIndex = dataNormalizada.length > 0 ? Math.min(Math.max(committedBrushRange.endIndex, brushStartIndex), dataNormalizada.length - 1) : 0;

	async function saveConfig(data: chartsConfigData, id_barragem: string) {
		try {
			const res = await chartsConf.create(id_barragem, data);
			if (res instanceof Error) {
				alert('Erro ao carregar configuracoes do grafico!', 'error');
			} else {
				alert('Configuracoes salvas!', 'success');
			}
		} catch (error) {
			alert('Erro ao carregar configuracoes do grafico!', 'error');
		}
	}

	const handleSaveConfig = async () => {
		const config = {
			eixo_y_max: yMaxAplicado,
			eixo_y_min: yMinAplicado,
		};
		const jsonConfig = JSON.stringify(config);

		const dataConf = {
			tipo_grafico: tipo_instrumento,
			instrumentos_ativos: selecionados,
			configuracoes: jsonConfig,
		};
		await saveConfig(dataConf, barragem_id);
	};

	useEffect(() => {
		setSelecionados([]);
	}, [tipo_instrumento]);

	useEffect(() => {
		if (confGrafico) {
			const confJson = confGrafico[0].configuracoes;
			const conf = JSON.parse(confJson);
			setYMaxCustom(conf.eixo_y_max ?? '');
			setYMinCustom(conf.eixo_y_min ?? '');
			setYMaxAplicado(conf.eixo_y_max ?? null);
			setYMinAplicado(conf.eixo_y_min ?? null);
			setConfId(confGrafico[0].uuid !== undefined ? confGrafico[0].uuid : null);
		} else {
			setYMaxCustom('');
			setYMinCustom('');
			setYMaxAplicado(null);
			setYMinAplicado(null);
			setConfId(null);
		}
	}, [confGrafico, tipo_instrumento]);

	useEffect(() => {
		setCommittedBrushRange(initialBrushRange);
	}, [
		tipo_instrumento,
		datasLeitura.length,
		datasLeitura[0]?.getTime(),
		datasLeitura[datasLeitura.length - 1]?.getTime(),
		initialBrushRange.startIndex,
		initialBrushRange.endIndex,
	]);

	return (
		<div style={{ fontFamily: 'Roboto, sans-serif' }}>
			<div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px', marginBottom: '16px', alignItems: 'center' }}>
				<label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontWeight: 600 }}>
					<input
						type="checkbox"
						checked={todosSelecionados}
						onChange={toggleTodos}
						disabled={nomesInstrumentos.length === 0}
					/>
					<span>Marcar todos</span>
				</label>
				{instrumentosOrdenados.map((instrumento) => (
					<label key={instrumento.nome} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
						<input
							type="checkbox"
							checked={selecionados.includes(instrumento.nome)}
							onChange={() => toggleInstrumento(instrumento.nome)}
						/>
						<span>{instrumento.nome}</span>
					</label>
				))}
				<IconButton onClick={downloadChart} disabled={ativos.length === 0}>
					<Download />
				</IconButton>
			</div>

			<div style={{ display: 'flex', gap: '20px' }}>
				<div style={{ width: '200px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
					<Typography variant='h6'>Editar Eixo Y</Typography>

					<div>
						<Typography variant='caption'>Maximo</Typography>
						<Input
							type="number"
							value={yMaxCustom}
							onChange={(e) => setYMaxCustom(e.target.value)}
						/>
					</div>

					<div>
						<Typography variant='caption'>Minimo</Typography>
						<Input
							type="number"
							value={yMinCustom}
							onChange={(e) => setYMinCustom(e.target.value)}
						/>
					</div>

					<Button
						onClick={handleAplicar}
						disabled={!yMinCustom || !yMaxCustom}
						variant='contained'
						startIcon={<Check />}
					>
						Aplicar
					</Button>

					<div style={{ width: '100%', display: 'flex', flexDirection: 'row', justifyContent: 'space-between', gap: 4 }}>
						<Button
							onClick={handleSaveConfig}
							variant='contained'
							startIcon={<Save />}
							disabled={yMaxAplicado === null || yMinAplicado === null}
						>
							Salvar
						</Button>
						<Button
							onClick={handleLimpar}
							variant='contained'
							style={{ backgroundColor: '#d13838' }}
							startIcon={<Clear />}
						>
							Limpar
						</Button>
					</div>
				</div>

				<div ref={chartRef} style={{ flex: 1 }}>
					{loading ? (
						<div style={{
							display: 'flex',
							justifyContent: 'center',
							alignItems: 'center',
							height: '300px'
						}}>
							<div style={{
								border: '4px solid #f3f3f3',
								borderTop: '4px solid #1976d2',
								borderRadius: '50%',
								width: '40px',
								height: '40px',
								animation: 'spin 1s linear infinite'
							}} />
							<style>{`
								@keyframes spin {
									0% { transform: rotate(0deg); }
									100% { transform: rotate(360deg); }
								}
							`}</style>
						</div>
					) : (
						<>
							<ResponsiveContainer width="100%" height={300}>
								<LineChart data={dataNormalizada}>
									{instrumentoUnico && (
										<>
											{instrumentoUnico.limites.verde !== null && (
												<ReferenceArea
													y2={Math.min(instrumentoUnico.limites.verde, maxYFinal)}
													fill="green"
													fillOpacity={0.2}
												/>
											)}
											{instrumentoUnico.limites.verde !== null && instrumentoUnico.limites.amarelo !== null && (
												<ReferenceArea
													y1={Math.max(instrumentoUnico.limites.verde, minYFinal)}
													y2={Math.min(instrumentoUnico.limites.amarelo, maxYFinal)}
													fill="yellow"
													fillOpacity={0.2}
												/>
											)}
											{instrumentoUnico.limites.amarelo !== null && instrumentoUnico.limites.laranja !== null && (
												<ReferenceArea
													y1={Math.max(instrumentoUnico.limites.amarelo, minYFinal)}
													y2={Math.min(instrumentoUnico.limites.laranja, maxYFinal)}
													fill="orange"
													fillOpacity={0.2}
												/>
											)}
											{instrumentoUnico.limites.laranja !== null && instrumentoUnico.limites.vermelho !== null && (
												<ReferenceArea
													y1={Math.max(instrumentoUnico.limites.laranja, minYFinal)}
													y2={Math.min(instrumentoUnico.limites.vermelho, maxYFinal)}
													fill="red"
													fillOpacity={0.2}
												/>
											)}
										</>
									)}
									<XAxis
									dataKey="data_leitura"
									fontFamily='Roboto'
									height={60}
									interval="preserveStartEnd"
									tick={({ x, y, payload }: { x: number; y: number; payload: { value: string } }) => (
											<g transform={`translate(${x},${y})`}>
												<text
													x={0}
													y={0}
													dy={10}
													textAnchor="end"
													fill="#666"
													fontSize={11}
													fontFamily="Roboto"
													transform="rotate(-45)"
												>
													{formatChartDate(payload.value)}
												</text>
											</g>
										)}
									/>
									<YAxis
										domain={[minYFinal, maxYFinal]}
										label={{
											value: legenda(legendaY),
											angle: -90,
											dx: -30
										}}
										tickFormatter={(value) => Number(value).toFixed(2).replace('.', ',')}
									/>
									<Tooltip
										labelFormatter={formatChartDate}
										formatter={(value: any) => value != null ? Number(value).toFixed(2).replace('.', ',') : ''}
									/>
									<Brush
										dataKey="data_leitura"
										startIndex={brushStartIndex}
										endIndex={brushEndIndex}
										onDragEnd={handleBrushDragEnd}
										tickFormatter={formatChartDate}
									/>
									{ativos.map((instrumento) => (
										<Line
											key={instrumento.nome}
											type="monotone"
											dataKey={instrumento.nome}
											stroke={instrumento.corLinha}
											strokeWidth={2}
											dot={{ r: 2 }}
											connectNulls
										/>
									))}
								</LineChart>
							</ResponsiveContainer>
							{instrumentoUnico && (
								<div style={{ marginTop: '48px' }}>
									<h2 style={{ fontSize: '20px', marginBottom: '16px' }}>
										Limites
									</h2>
									<ul style={{ listStyle: 'disc', listStylePosition: 'inside', fontSize: '14px' }}>
										<li>
											Limite Normal: {formatLimitValue(instrumentoUnico.limites.verde)}
										</li>
										<li>
											Limite Atencao: {formatLimitValue(instrumentoUnico.limites.amarelo)}
										</li>
										<li>
											Limite Alerta: {formatLimitValue(instrumentoUnico.limites.laranja)}
										</li>
										<li>
											Limite Emergencia: {formatLimitValue(instrumentoUnico.limites.vermelho)}
										</li>
									</ul>
								</div>
							)}
						</>
					)}
				</div>
			</div>
		</div>
	);
};

export default GraficoLinha;
